import PageWrapper from "../../components/common/PageWrapper";
import Header from "../../components/common/Header";
import BodyWrapper from "../../components/common/BodyWrapper";
import Footer from "../../components/common/Footer";
import Wrapper from "../../components/dashboard/dashboardPartials/Wrapper";
import Dashboard from "../../components/dashboard/dashboardPartials/Dashboard";
import { useGeneral } from "../../components/contexts/GeneralContext";
import { useEffect, useState } from "react";
import axios from "../../config/axios";
import FullScreenLoading from "../../components/common/loading/FullScreenLoading";
import ApiErrorMessage from "../../components/common/ApiErrorMessage";
import NoItemsFound from "../../components/common/NoItemsFound";
import MySubscriptions from "../../components/dashboard/dashboardPartials/MySubscriptions";
import GeneralButton from "../../components/common/GeneralButton";
import SearchBar from "../../components/common/SearchBar";
import SelectBox from "../../components/common/SelectBox";
import { useLocation } from "react-router-dom";
import MyOrders from "../../components/dashboard/dashboardPartials/MyOrders";

// ✅ Custom hook to fetch paginated content
const usePageContent = (page, filters) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    items: [],
    pagination: {},
  });
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();
  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const params = { page };
        if (filters.search) params.q = filters.search;
        if (filters.sort) params.sort = filters.sort;
        if (filters.status) params.status = filters.status;
        const { data } = await axios.get("/dashboard/orders", {
          params,
          signal: controller.signal,
        });

        if (active) {
          setContent((prev) => ({
            items: page === 1 ? data.items : [...prev.items, ...data.items],
            pagination: data?.pagination,
          }));
         
        }
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error(err);
          if (active)
            setError("Failed to load products. Please try again later.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [page, filters]);

  return { content, loading, error, discountCoupons };
};

// ✅ Auto-scroll on initial load
const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

// ✅ Main Page Component
const MyOrdersPage = () => {
  const location = useLocation();
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      sort: params.get("sort") || "",
      status: params.get("status") || "",
    };
  };
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(getFiltersFromURL);
  const [inputValue, setInputValue] = useState(filters.search || "");
  const { content, loading, error, pagination, discountCoupons } =
    usePageContent(page, filters);
  useScrollToTop();
  // ✅ Clean filter handlers
  const handleClearSearch = () => {
    setInputValue("");
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  const handleSearch = (searchQuery) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, search: searchQuery }));
  };

  const handleSortChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, sort: val }));
  };

  // ✅ Corrected — SelectBox returns string value, not object
  const handleStatusChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, status: val }));
  };

  const handleNext = () => {
    if (content?.pagination?.pageCount && page < content.pagination.pageCount) {
      setPage((prev) => prev + 1);
    }
  };

  const sortOptions = [
    { label: "Newest to Oldest", value: "1" },
    { label: "Oldest to Newest", value: "-1" },
  ];

  return (
    <>
      {loading && page === 1 && <FullScreenLoading />}
      <PageWrapper loading={false} className="bg-bg min-h-screen">
        <Header code={discountCoupons} />

        <BodyWrapper className="mb-0 xl:mb-0">
          <Wrapper
            component={
              <>
                {content?.pagination?.totalItems > 0 && (
                  <div className="mb-4 flex flex-col items-start md:items-center justify-between gap-2 md:mt-2 mt-0">
                    <div className="flex-1 flex flex-col md:flex-row items-center justify-end gap-2 w-full">
                      <div className="flex  flex-col md:flex-row items-center justify-end gap-4 w-full xl:w-[75%]">
                        <SearchBar
                          value={inputValue}
                          setValue={setInputValue}
                          onClear={handleClearSearch}
                          onSearch={handleSearch}
                        />

                        <SelectBox
                          value={filters.sort}
                          setValue={handleSortChange}
                          options={sortOptions}
                          placeholder="Sort By"
                        />
                        <SelectBox
                          value={filters.status}
                          setValue={handleStatusChange}
                          options={[
                            { label: "Pending", value: "0" },
                            { label: "Delivered", value: "1" },
                            { label: "Cancelled", value: "2" },
                          ]}
                          placeholder="Select Status"
                        />
                      </div>
                    </div>

                    {/* Applied filters summary, unchanged */}
                    {(filters.search || filters.status || filters.sort) && (
                      <div className="flex flex-wrap items-center gap-2 mt-2 px-3 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                        {filters.search && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-700">
                            <span className="text-secondary"> Search: </span>
                            <span className="text-[#D06F36]">
                              "{filters.search}"
                            </span>
                            <button
                              onClick={() =>
                                setFilters({ ...filters, search: "" })
                              }
                              title="Click to remove search"
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {filters.status && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                            <span className="text-secondary">Filter: </span>
                            {filters.status === "0"
                              ? "Pending"
                              : filters.status === "1"
                              ? "Delivered"
                              : filters.status === "2" && "Cancelled"}
                            <button
                              onClick={() => {
                                setFilters({ ...filters, status: "" });
                              }}
                              title="Click to remove sort "
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {/* Sort Filter */}
                        {filters.sort && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                            <span className="text-secondary">Sort: </span>
                            {
                              sortOptions.find((s) => s.value === filters.sort)
                                ?.label
                            }
                            <button
                              onClick={() => {
                                setFilters({ ...filters, sort: "" });
                              }}
                              title="Click to remove sort "
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {/* Clear All */}
                        <button
                          onClick={() => {
                            handleClearSearch();

                            setFilters({
                              sort: "",
                              status: "",
                            });
                          }}
                          className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                        >
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!loading && error ? (
                  <ApiErrorMessage
                    message1="Failed to load orders"
                    message2="Please check your connection and try again."
                  />
                ) : !loading && !content?.items?.length ? (
                  <NoItemsFound
                    message1="No Orders Found"
                    message2={
                      filters.search || filters.status
                        ? "Try a different query or filter"
                        : " Make a purchase to see more details."
                    }
                  />
                ) : (
                  content && (
                    <>
                      <MyOrders account={content.items} />

                      {/* Pagination controls */}
                      {content?.pagination?.pageCount > 1 &&
                        page < content?.pagination.pageCount && (
                          <div className="col-span-full flex justify-center w-full mt-2 xl:mt-4">
                            <GeneralButton
                              text={loading ? "Loading..." : "Load More"}
                              title="Click to load more items"
                              onClick={handleNext}
                              disabled={loading}
                              bgColor="#D06F36"
                            />
                          </div>
                        )}

                      {/* Spinner for loading more */}
                      {loading && page > 1 && (
                        <div className="col-span-full flex justify-center mt-3">
                          <div className="w-5 h-5 border-4 border-tertiary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </>
                  )
                )}
              </>
            }
          />
        </BodyWrapper>

        <Footer />
      </PageWrapper>
    </>
  );
};

export default MyOrdersPage;
